import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("lg_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const auth = {
  signup: (data) => api.post("/auth/signup", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  upgrade: () => api.post("/auth/upgrade").then((r) => r.data),
  demo: () => api.post("/auth/demo").then((r) => r.data),
  forgotPassword: (email) =>
    api.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) =>
    api.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  changePassword: (current_password, new_password) =>
    api
      .post("/auth/change-password", { current_password, new_password })
      .then((r) => r.data),
};

export const profile = {
  updateSmsPreferences: ({ phone, sms_opt_in }) =>
    api
      .put("/profile/sms-preferences", { phone, sms_opt_in })
      .then((r) => r.data),
};

export const publicApi = {
  smsOptIn: ({ phone, sms_opt_in }) =>
    api
      .post("/public/sms-optin", { phone, sms_opt_in })
      .then((r) => r.data),
  sponsorLanding: (slug) =>
    api.get(`/sponsor/landing/${slug}`).then((r) => r.data),
  sponsorTrack: ({ referral_code, event_type, path, metadata }) =>
    api
      .post("/sponsor/track", { referral_code, event_type, path, metadata })
      .then((r) => r.data),
  contact: ({ name, email, subject, message }) =>
    api
      .post("/contact", { name, email, subject, message })
      .then((r) => r.data),
};

export const adminApi = {
  whoami: () => api.get("/admin/whoami").then((r) => r.data),
  overview: () => api.get("/admin/overview").then((r) => r.data),
  kpis: (params = {}) =>
    api.get("/admin/kpis", { params }).then((r) => r.data),
  ecosystem: () => api.get("/admin/ecosystem").then((r) => r.data),
  masterOverview: ({ include_test } = {}) =>
    api
      .get("/admin/master-overview", { params: include_test ? { include_test: 1 } : {} })
      .then((r) => r.data),
  proverbAnalytics: ({ range } = {}) =>
    api.get("/admin/proverb-analytics", { params: range ? { range } : {} }).then((r) => r.data),
  proverbStatus: () =>
    api.get("/admin/proverb/integration-status").then((r) => r.data),
  topicInsights: ({ range } = {}) =>
    api.get("/admin/topic-insights", { params: range ? { range } : {} }).then((r) => r.data),
  lifeguideAnalytics: ({ range, include_test } = {}) =>
    api
      .get("/admin/lifeguide-analytics", {
        params: { ...(range ? { range } : {}), ...(include_test ? { include_test: 1 } : {}) },
      })
      .then((r) => r.data),
  pwaAnalytics: ({ range, include_test } = {}) =>
    api
      .get("/admin/pwa/analytics", {
        params: { ...(range ? { range } : {}), ...(include_test ? { include_test: 1 } : {}) },
      })
      .then((r) => r.data),
  analyticsDiagnostics: ({ range, include_test, collection, event_type } = {}) =>
    api
      .get("/admin/analytics/diagnostics", {
        params: {
          ...(range ? { range } : {}),
          ...(include_test ? { include_test: 1 } : {}),
          ...(collection ? { collection } : {}),
          ...(event_type ? { event_type } : {}),
        },
      })
      .then((r) => r.data),
  proverbCardOrders: ({ include_unpaid, include_test, status, since, until, limit, cursor } = {}) =>
    api
      .get("/admin/proverb/card-orders", {
        params: {
          ...(status ? { status } : {}),
          ...(include_unpaid ? { include_unpaid: 1 } : {}),
          ...(include_test ? { include_test: 1 } : {}),
          ...(since ? { since } : {}),
          ...(until ? { until } : {}),
          ...(limit ? { limit } : {}),
          ...(cursor ? { cursor } : {}),
        },
      })
      .then((r) => r.data),
  revenue: ({ range, include_test } = {}) =>
    api
      .get("/admin/revenue", {
        params: { ...(range ? { range } : {}), ...(include_test ? { include_test: 1 } : {}) },
      })
      .then((r) => r.data),
  users: (params = {}) => api.get("/admin/users", { params }).then((r) => r.data),
  markTestSponsors: () => api.post("/admin/sponsors/mark-test").then((r) => r.data),
  performance: ({ range, sponsor_type, include_test } = {}) => {
    const p = new URLSearchParams();
    if (range) p.set("range", range);
    if (sponsor_type) p.set("sponsor_type", sponsor_type);
    if (include_test) p.set("include_test", "1");
    const qs = p.toString();
    return api.get(`/admin/sponsor-performance${qs ? "?" + qs : ""}`).then((r) => r.data);
  },
  listSponsors: () => api.get("/admin/sponsors").then((r) => r.data),
  getSponsor: (id) => api.get(`/admin/sponsors/${id}`).then((r) => r.data),
  createSponsor: (payload) =>
    api.post("/admin/sponsors", payload).then((r) => r.data),
  editSponsor: (id, payload) =>
    api.patch(`/admin/sponsors/${id}`, payload).then((r) => r.data),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/admin/sponsors/${id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  removeLogo: (id) =>
    api.delete(`/admin/sponsors/${id}/logo`).then((r) => r.data),
  invite: (id, email) =>
    api.post(`/admin/sponsors/${id}/invite`, { email }).then((r) => r.data),
  listAdmins: (id) =>
    api.get(`/admin/sponsors/${id}/admins`).then((r) => r.data),
  listInvites: (id) =>
    api.get(`/admin/sponsors/${id}/invites`).then((r) => r.data),
  revokeAdmin: (sponsor_id, user_id) =>
    api.delete(`/admin/sponsors/${sponsor_id}/admins/${user_id}`).then((r) => r.data),
  systemHealth: () => api.get("/admin/system-health").then((r) => r.data),
  smsAnalytics: () => api.get("/admin/sms-analytics").then((r) => r.data),
  smsEligibleRecipients: () =>
    api.get("/admin/sms/eligible-recipients").then((r) => r.data),
  smsSendTest: ({ user_id }) =>
    api.post("/admin/sms/send-test", { user_id }).then((r) => r.data),
  trialReminders: ({ include_test } = {}) =>
    api
      .get("/admin/trial-reminders", {
        params: include_test ? { include_test: 1 } : {},
      })
      .then((r) => r.data),
  trialRemindersProcess: () =>
    api.post("/admin/trial-reminders/process").then((r) => r.data),
  trialRemindersPrepareSelf: () =>
    api.post("/admin/trial-reminders/prepare-48h-self").then((r) => r.data),
  trialRemindersPrepareExpiredSelf: () =>
    api.post("/admin/trial-reminders/prepare-expired-self").then((r) => r.data),
  proverbIntegrationStatus: () =>
    api.get("/admin/proverb/integration-status").then((r) => r.data),
};

export const sponsorApi = {
  attribute: (referral_code) =>
    api.post("/sponsor/attribute", { referral_code }).then((r) => r.data),
  dashboard: (sponsor_id, range) => {
    const qs = range ? `?range=${encodeURIComponent(range)}` : "";
    return api.get(`/sponsor/${sponsor_id}/dashboard${qs}`).then((r) => r.data);
  },
  peekInvite: (token) =>
    api.get(`/sponsor-invite/${token}`).then((r) => r.data),
  acceptInvite: (token, payload) =>
    api.post(`/sponsor-invite/${token}/accept`, payload).then((r) => r.data),
};

export const guidance = {
  send: (text, history) => api.post("/guidance", { text, history }).then((r) => r.data),
  history: () => api.get("/guidance/history").then((r) => r.data),
  sample: (text, history) => api.post("/guidance/sample", { text, history }).then((r) => r.data),
};

export const daily = {
  get: () => api.get("/daily").then((r) => r.data),
  audioUrl: () => `${API}/daily/audio`,
  audio: () =>
    api
      .get("/daily/audio", { responseType: "blob" })
      .then((r) => URL.createObjectURL(r.data)),
};

export const coach = {
  send: (message, session_id) =>
    api.post("/chat", { message, session_id }).then((r) => r.data),
  history: (session_id) =>
    api
      .get("/chat/history", { params: session_id ? { session_id } : {} })
      .then((r) => r.data),
  // Public guest-preview coach — no auth. Frontend passes running local
  // history so the reply feels continuous.
  sample: ({ message, guest_id, history }) =>
    api
      .post("/coach/sample", { message, guest_id, history })
      .then((r) => r.data),
};

export const guest = {
  track: ({ event_type, guest_id, from_proverb, proverb_topic, metadata }) =>
    api
      .post("/guest/track", { event_type, guest_id, from_proverb, proverb_topic, metadata })
      .then((r) => r.data)
      .catch(() => null), // fire-and-forget, never breaks UX
};

export const analyze = {
  run: ({ message, audience, prior_message, extra_context }) =>
    api
      .post("/analyze", { message, audience, prior_message, extra_context })
      .then((r) => r.data),
  sample: ({ message, audience, prior_message, extra_context }) =>
    api
      .post("/analyze/sample", { message, audience, prior_message, extra_context })
      .then((r) => r.data),
};

export const scripture = {
  ask: (query) => api.post("/scripture", { query }).then((r) => r.data),
};

export const decision = {
  ask: (situation, options) =>
    api.post("/decision", { situation, options }).then((r) => r.data),
};

export const metrics = {
  upsert: (body) => api.post("/metrics", body).then((r) => r.data),
  week: () => api.get("/metrics/week").then((r) => r.data),
  today: () => api.get("/metrics/today").then((r) => r.data),
};

export const quotaApi = {
  get: () => api.get("/quota").then((r) => r.data),
};

export const subscription = {
  packages: () => api.get("/subscription/packages").then((r) => r.data),
  status: () => api.get("/subscription/status").then((r) => r.data),
};

export const config = {
  features: () => api.get("/config/features").then((r) => r.data),
};

export const gifts = {
  create: ({
    recipient_email,
    recipient_name,
    recipient_phone,
    delivery_method,
    duration_months,
    is_anonymous,
    message,
    origin_url,
    send_at,
    recipient_timezone,
  }) =>
    api
      .post("/gifts/create", {
        recipient_email,
        recipient_name,
        recipient_phone,
        delivery_method,
        duration_months,
        is_anonymous,
        message,
        origin_url,
        send_at,
        recipient_timezone,
      })
      .then((r) => r.data),
  guestCreate: (payload) =>
    api.post("/gifts/guest-create", payload).then((r) => r.data),
  session: (sessionId) =>
    api.get(`/gifts/session/${sessionId}`).then((r) => r.data),
  mine: () => api.get("/gifts/mine").then((r) => r.data),
  lookup: (token) => api.get(`/gifts/lookup/${token}`).then((r) => r.data),
  redeem: (token) => api.post(`/gifts/redeem/${token}`).then((r) => r.data),
};

export const checkout = {
  create: (tier, origin_url) =>
    api.post("/checkout/create", { tier, origin_url }).then((r) => r.data),
  status: (session_id) =>
    api.get(`/checkout/status/${session_id}`).then((r) => r.data),
};

export const wisdomPath = {
  latest: () => api.get("/wisdom-path/latest").then((r) => r.data),
  consume: (id) => api.post(`/wisdom-path/${id}/consume`).then((r) => r.data),
};

export const journals = {
  list: () => api.get("/journals").then((r) => r.data),
  get: (id) => api.get(`/journals/${id}`).then((r) => r.data),
  create: ({ title, content, source, source_ref, week_start, week_end, reflection }) =>
    api
      .post("/journals", { title, content, source, source_ref, week_start, week_end, reflection })
      .then((r) => r.data),
  update: (id, { title, content, source_ref, reflection, week_start, week_end }) =>
    api
      .put(`/journals/${id}`, { title, content, source_ref, reflection, week_start, week_end })
      .then((r) => r.data),
  remove: (id) => api.delete(`/journals/${id}`).then((r) => r.data),
  weekly: {
    current: () =>
      api.get("/journals/weekly-reflection/current").then((r) => r.data),
    generate: () =>
      api.post("/journals/weekly-reflection/generate").then((r) => r.data),
  },
};

export const savedWisdom = {
  list: (kind) =>
    api.get("/saved-wisdom", { params: kind ? { kind } : {} }).then((r) => r.data),
  exists: (kind, reference_id) =>
    api
      .get("/saved-wisdom/exists", { params: { kind, reference_id } })
      .then((r) => r.data),
  create: ({ kind, reference_id, title, body, meta }) =>
    api
      .post("/saved-wisdom", { kind, reference_id, title, body, meta })
      .then((r) => r.data),
  remove: (id) => api.delete(`/saved-wisdom/${id}`).then((r) => r.data),
};

export const pwa = {
  trackEvent: (payload) =>
    api.post("/pwa/events", payload).then((r) => r.data),
};
